import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';

const UserRoutes = () => {
  const { user } = useContext(AuthContext);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/user/routes');
      console.log('Fetched user routes:', res.data.length);
      setRoutes(res.data);
    } catch (error) {
      console.error('Error fetching routes:', error);
      alert('Error al cargar rutas: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const getStatusBadge = (route) => {
    // Helper to get user ID as string - handles all formats
    const getUserIdString = (userOrId) => {
      if (!userOrId) return null;
      if (typeof userOrId === 'string') return userOrId;
      // If it's a populated object with _id
      if (userOrId._id) {
        const id = userOrId._id;
        return typeof id === 'string' ? id : id.toString();
      }
      // If it's an ObjectId
      if (typeof userOrId.toString === 'function') return userOrId.toString();
      return String(userOrId);
    };

    // Check if route is assigned to current user - try multiple methods
    let currentUserId = null;
    if (user?._id) {
      currentUserId = getUserIdString(user._id);
    }
    
    let assignedUserId = null;
    if (route.assignedTo) {
      assignedUserId = getUserIdString(route.assignedTo);
    }
    
    // Compare IDs - try multiple methods
    let isAssignedToUser = false;
    if (currentUserId && assignedUserId) {
      // Direct comparison
      isAssignedToUser = currentUserId === assignedUserId;
      
      // If not equal, try comparing after trimming
      if (!isAssignedToUser) {
        isAssignedToUser = currentUserId.trim() === assignedUserId.trim();
      }
      
      // If still not equal, try substring matching (in case of nested IDs)
      if (!isAssignedToUser && (currentUserId.includes(assignedUserId) || assignedUserId.includes(currentUserId))) {
        isAssignedToUser = true;
      }
    }
    
    // Debug logging
    console.log('Status Badge Check:', {
      routeNumber: route.routeNumber,
      currentUserId,
      assignedUserId,
      isAssignedToUser,
      routeStatus: route.status,
      assignedTo: route.assignedTo
    });
    
    let displayText;
    let statusClass;
    
    // Priority 1: If assigned to current user (check both frontend comparison and backend flag), show status based on route status
    // Use backend flag if available, otherwise use frontend comparison
    const isAssigned = route.isAssignedToCurrentUser !== undefined ? route.isAssignedToCurrentUser : isAssignedToUser;
    
    if (isAssigned) {
      // If route is pending, show "Pending (Selected)" with yellow color (same as admin)
      if (route.status === 'pending') {
        displayText = 'Pendiente (Seleccionada)';
        statusClass = 'badge badge-pending';
      } else {
        // For other statuses, use admin's badge classes
        const statusMap = {
          'approved': 'Aprobada',
          'pending-acceptance': 'Pendiente Aceptación',
          'accepted': 'Aceptada',
          'pending-approval': 'Pendiente Aprobación',
          'completed': 'Completada',
          'rejected': 'Rechazada',
          'returned': 'Devuelta',
          'in-progress': 'En Progreso'
        };
        displayText = statusMap[route.status] || route.status || 'Aceptada';
        
        // Map status to admin's badge class
        let badgeClass = 'badge-pending';
        if (route.status === 'approved') badgeClass = 'badge-approved';
        else if (route.status === 'accepted') badgeClass = 'badge-accepted';
        else if (route.status === 'in-progress') badgeClass = 'badge-in-progress';
        else if (route.status === 'pending-approval') badgeClass = 'badge-pending-approval';
        else if (route.status === 'pending-acceptance') badgeClass = 'badge-pending-acceptance';
        else if (route.status === 'completed') badgeClass = 'badge-completed';
        else if (route.status === 'rejected') badgeClass = 'badge-rejected';
        else if (route.status === 'returned') badgeClass = 'badge-warning';
        
        statusClass = `badge ${badgeClass}`;
      }
    }
    // Priority 2: Use displayStatus if available (for routes assigned to others)
    else if (route.displayStatus) {
      // Map display statuses to user-friendly text
      if (route.displayStatus === 'not-selected') {
        displayText = 'No Seleccionada';
        statusClass = 'badge badge-rejected';
      } else if (route.displayStatus === 'assigned-to-other') {
        displayText = 'Asignada a Otro';
        statusClass = 'badge badge-secondary';
      } else if (route.displayStatus === 'rejected') {
        displayText = 'Rechazada';
        statusClass = 'badge badge-rejected';
      } else {
        displayText = route.displayStatus;
        // Map to admin badge classes
        let badgeClass = 'badge-pending';
        if (route.displayStatus === 'approved') badgeClass = 'badge-approved';
        else if (route.displayStatus === 'accepted') badgeClass = 'badge-accepted';
        else if (route.displayStatus === 'in-progress') badgeClass = 'badge-in-progress';
        else if (route.displayStatus === 'pending-approval') badgeClass = 'badge-pending-approval';
        else if (route.displayStatus === 'pending-acceptance') badgeClass = 'badge-pending-acceptance';
        else if (route.displayStatus === 'completed') badgeClass = 'badge-completed';
        else if (route.displayStatus === 'rejected') badgeClass = 'badge-rejected';
        else if (route.displayStatus === 'returned') badgeClass = 'badge-warning';
        statusClass = `badge ${badgeClass}`;
      }
    }
    // Priority 3: Use route status (use admin's badge classes)
    else {
      const statusMap = {
        'pending': 'Pendiente',
        'approved': 'Aprobada',
        'pending-acceptance': 'Pendiente Aceptación',
        'accepted': 'Aceptada',
        'in-progress': 'En Progreso',
        'pending-approval': 'Pendiente Aprobación',
        'completed': 'Completada',
        'rejected': 'Rechazada'
      };
      displayText = statusMap[route.status] || route.status || 'Pendiente';
      
      // Map status to admin's badge class
      let badgeClass = 'badge-pending';
      if (route.status === 'approved') badgeClass = 'badge-approved';
      else if (route.status === 'accepted') badgeClass = 'badge-accepted';
      else if (route.status === 'in-progress') badgeClass = 'badge-in-progress';
      else if (route.status === 'pending-approval') badgeClass = 'badge-pending-approval';
      else if (route.status === 'pending-acceptance') badgeClass = 'badge-pending-acceptance';
      else if (route.status === 'completed') badgeClass = 'badge-completed';
      else if (route.status === 'rejected') badgeClass = 'badge-rejected';
      else if (route.status === 'returned') badgeClass = 'badge-warning';
      
      statusClass = `badge ${badgeClass}`;
    }
    
    return <span className={statusClass} style={{ textTransform: 'none' }}>{displayText}</span>;
  };

  if (loading) {
    return <div className="loading">Cargando rutas...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0 }}>Mis Rutas</h2>
        <button className="btn btn-secondary" onClick={fetchRoutes} style={{ minWidth: '100px' }}>
          Actualizar
        </button>
      </div>
      {routes.length === 0 ? (
        <div className="card">
          <p>No hay rutas disponibles. Las rutas aparecerán aquí una vez que el administrador las designe.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="table responsive-table">
          <thead>
            <tr>
              <th>Número de Ruta</th>
              <th>Destino</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {routes.map(route => (
              <tr key={route._id}>
                <td data-label="Número de Ruta">{route.routeNumber}</td>
                <td data-label="Destino">{route.endPoint}</td>
                <td data-label="Estado">{getStatusBadge(route)}</td>
                <td data-label="Acciones">
                  <Link to={`/user/routes/${route._id}`} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '14px', width: '100%' }}>
                    Ver Detalles
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserRoutes;
